import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Send, Pencil, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Badge, SearchInput, EmptyState, Card } from "@/components/ui-em";

const EMPTY = { name: "", link: "", description: "", category: "General" };

export default function AdminTelegram() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    setLoading(true);
    api.get("/telegram/channels", { params: { raw: 1 } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/telegram/categories").then((r) => setCategories(r.data));
  }, []);

  const filtered = items.filter((i) => (!q ? true : i.name.toLowerCase().includes(q.toLowerCase())));

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({ name: item.name, link: item.link, description: item.description || "", category: item.category });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/telegram/channels/${editing}`, form);
        toast.success("Channel updated");
      } else {
        await api.post("/telegram/channels", form);
        toast.success("Channel added");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this channel?")) return;
    try {
      await api.delete(`/telegram/channels/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · Community"
        title="Telegram Channels"
        description="Curate the official Telegram channels leaders should know about."
        actions={<Button icon={Plus} onClick={openNew} size="lg" data-testid="admin-new-telegram">New channel</Button>}
      />

      <SearchInput value={q} onChange={setQ} placeholder="Search channels..." />

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-zinc-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Send}
            title="No channels yet"
            description="Add the first Telegram channel so leaders can join the community."
            actionLabel="Add channel"
            onAction={openNew}
            className="m-4"
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold text-left">
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Link</th>
                <th className="px-6 py-4">Created</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "140ms" }}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Send className="w-3.5 h-3.5 text-[#229ED9]" />
                      <span className="text-white">{c.name}</span>
                    </div>
                    {c.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-1 max-w-md">{c.description}</div>}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" size="xs" uppercase>{c.category}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <a
                      href={c.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-gold hover:underline max-w-[240px] truncate"
                    >
                      <span className="truncate">{c.link.replace(/^https?:\/\//, "")}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </td>
                  <td className="px-6 py-4 text-xs text-zinc-500">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => openEdit(c)}
                      data-testid={`edit-telegram-${c.id}`}
                      className="p-2 rounded-md text-zinc-500 hover:text-gold hover:bg-gold/10 mr-1"
                      style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`delete-telegram-${c.id}`}
                      className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                      style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-ink-0/70 backdrop-blur-md overflow-y-auto">
          <div className="em-card p-8 w-full max-w-2xl my-8" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl">{editing ? "Edit channel" : "New Telegram channel"}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <input
                required
                placeholder="Channel name (e.g., EM Official)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="em-input"
                data-testid="telegram-name"
              />
              <input
                required
                type="url"
                placeholder="https://t.me/yourchannel"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                className="em-input"
                data-testid="telegram-link"
              />
              <textarea
                placeholder="Short description of what this channel is for..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="em-input"
                rows={3}
                data-testid="telegram-description"
              />
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="em-input"
                data-testid="telegram-category"
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Button type="submit" variant="primary" size="lg" className="w-full" data-testid="telegram-submit">
                {editing ? "Save changes" : "Add channel"}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
